import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Reveal } from "./Reveal";
import { MapPin, Clock, CalendarDays, ArrowRight, HandHeart, ExternalLink, Package, Gift, Users, Sparkles } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { PAGES } from "@/i18n/extra_translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARTICIPATION = [
  { icon: Package, title: "Food Box Outreach", desc: "We deliver packaged food boxes to families' homes and bring food and blankets to neighbors sleeping on the streets.", schedule: "Ongoing, Central Florida" },
  { icon: Gift, title: "Toy Giveaways", desc: "We give toys to children with autism and children from low-income families, including visits to local hospitals.", schedule: "Throughout the year" },
  { icon: Users, title: "Community Events", desc: "We join church events, markets, and community gatherings across Central Florida. Invite us to yours!", schedule: "By invitation" },
];

const btnBase = "inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs tracking-[0.12em] uppercase transition-colors duration-300";

const UpcomingCard = ({ e, i }) => (
  <Reveal delay={i * 0.1} className="col-span-12 md:col-span-6">
    <div data-testid={`upcoming-event-${i}`} className="group h-full flex flex-col rounded-2xl bg-[#FAF9F6] border border-[#E2E8E4] p-8 transition-all duration-500 hover:border-[#F4A261]/60 hover:-translate-y-1">
      <span className="inline-block self-start rounded-full bg-[#F4A261]/15 text-[#F4A261] px-4 py-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold">{e.tag}</span>
      <h4 className="font-display text-2xl sm:text-3xl mt-5 text-[#1A2F24]">{e.title}</h4>
      {e.highlight && (
        <p className="mt-4 flex items-center gap-2 text-sm font-semibold text-[#F4A261]" data-testid={`upcoming-event-highlight-${i}`}>
          <Sparkles size={15} className="shrink-0" /> {e.highlight}
        </p>
      )}
      <p className="mt-3 text-sm leading-relaxed text-[#5C7368]">{e.description}</p>
      <p className="mt-3 text-sm italic text-[#1E352A]">{e.highlight ? "Our volunteers from Living Fruit Outreach and Smallest Souls Rescue will be there to meet you. Come say hi!" : "Our volunteers will be there to meet you. Come say hi!"}</p>
      <div className="mt-5 space-y-2 text-xs text-[#5C7368]">
        {e.date && <p className="flex items-center gap-2"><CalendarDays size={14} className="text-[#F4A261]" /> {e.date}</p>}
        {e.time && <p className="flex items-center gap-2"><Clock size={14} className="text-[#F4A261]" /> {e.time}</p>}
        {e.location && <p className="flex items-center gap-2"><MapPin size={14} className="text-[#F4A261]" /> {e.location}</p>}
      </div>
      <div className="mt-6 pt-2 mt-auto flex flex-wrap gap-3">
        {e.link_url && (
          <a href={e.link_url} target="_blank" rel="noopener noreferrer" data-testid={`upcoming-event-info-${i}`}
            className={`bg-[#1E352A] text-[#FAF9F6] hover:bg-[#F4A261] hover:text-white ${btnBase}`}>
            <ExternalLink size={14} /> {e.link_label || "Event Info"}
          </a>
        )}
        <a href="/#volunteer" data-testid={`upcoming-event-volunteer-${i}`}
          className={`border border-[#1E352A]/25 text-[#1E352A] hover:border-[#F4A261] hover:text-[#F4A261] ${btnBase}`}>
          <HandHeart size={14} /> Volunteer With Us
        </a>
      </div>
    </div>
  </Reveal>
);

const Events = () => {
  const { t, lang } = useLanguage();
  const pg = PAGES[lang] || PAGES.en;
  const [events, setEvents] = useState([]);
  useEffect(() => {
    axios.get(`${API}/events`).then((r) => setEvents(r.data.events || [])).catch(() => {});
  }, []);

  return (
    <section id="events" className="py-28 lg:py-36 bg-[#C4D7D1]/25 border-y border-[#E2E8E4]/70" data-testid="events-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="mb-14 max-w-3xl">
          <p className="text-xs tracking-[0.3em] uppercase text-[#5C7368] mb-6">{t("ev_kicker")}</p>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1A2F24]">{t("ev_title_a")}<em className="text-[#F4A261]">{t("ev_title_em")}</em></h2>
          <p className="mt-6 text-base leading-relaxed text-[#5C7368]" data-testid="events-intro">We show up wherever our community gathers. Living Fruit Outreach partners with churches, vendors, and local organizations to bring food, blankets, and toys to the people who need them most.</p>
        </Reveal>

        {events.length > 0 && (
          <div className="mb-16" data-testid="upcoming-events">
            <h3 className="text-xs tracking-[0.3em] uppercase text-[#5C7368] mb-6">Upcoming Events</h3>
            <div className="grid grid-cols-12 gap-6">
              {events.map((e, i) => <UpcomingCard key={e.id || i} e={e} i={i} />)}
            </div>
          </div>
        )}

        <h3 className="text-xs tracking-[0.3em] uppercase text-[#5C7368] mb-6">What We Take Part In</h3>
        <div className="grid grid-cols-12 gap-6">
          {PARTICIPATION.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.1} className="col-span-12 md:col-span-4">
              <div data-testid={`participation-card-${i}`} className="group h-full rounded-2xl bg-[#FAF9F6] border border-[#E2E8E4] p-8 transition-all duration-500 hover:border-[#F4A261]/60 hover:-translate-y-1">
                <span className="inline-flex w-11 h-11 rounded-full bg-[#C4D7D1] items-center justify-center"><p.icon size={20} className="text-[#1E352A]" /></span>
                <h4 className="font-display text-2xl sm:text-3xl mt-5 text-[#1A2F24]">{p.title}</h4>
                <p className="mt-3 text-sm leading-relaxed text-[#5C7368]">{p.desc}</p>
                <p className="mt-5 flex items-center gap-2 text-xs tracking-[0.12em] uppercase text-[#F4A261]"><CalendarDays size={14} /> {p.schedule}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2} className="mt-12">
          <p className="text-sm text-[#5C7368]">
            Hosting a community event? We'd love to partner with you. <a href="#contact" data-testid="events-vendor-link" className="text-[#F4A261] underline underline-offset-4 hover:text-[#1E352A] transition-colors duration-300">Reach out!</a>
          </p>
        </Reveal>
        <Reveal delay={0.25} className="mt-10">
          <Link to="/past-events" data-testid="events-see-past-btn" className="inline-flex items-center gap-2 rounded-full bg-[#1E352A] text-[#FAF9F6] px-8 py-4 text-sm tracking-[0.12em] uppercase transition-colors duration-300 hover:bg-[#F4A261] hover:text-white">
            {pg.btn_see_past} <ArrowRight size={15} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

export default Events;
